import { useMemo, useState } from "react";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { trpc } from "../../lib/trpc";
import type { OverviewItem } from "./StockOverviewSection";

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}

const TYPE_LABELS: Record<string, string> = {
  consumption: "Üretim tüketimi",
  shipment: "Sevkiyat",
  scrap: "Hurda / imha",
};

/** Son N günün TR-yerel gün anahtarları (bugün dahil, eskiden yeniye). */
function lastDays(n: number): string[] {
  const out: string[] = [];
  const now = new Date(Date.now() + 3 * 3600 * 1000); // TR duvar saati (UTC+3, DST yok)
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 86400000);
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`);
  }
  return out;
}

/** Tüketim trendi draweri: 1/7/30/90 gün toplamları + yeterlilik + günlük bar grafiği + tür kırılımı. */
export function ConsumptionTrendDrawer({ open, item, onClose }: {
  open: boolean;
  item: OverviewItem | null;
  onClose: () => void;
}) {
  const [range, setRange] = useState<30 | 90>(30);
  const trendQ = trpc.inventory.consumptionTrend.useQuery(
    { itemKind: item?.kind ?? "stock", itemId: item?.id ?? "" },
    { enabled: open && !!item },
  );
  const d = trendQ.data;
  const uom = d?.item?.uom ?? item?.uom ?? "";

  // Günlük seri: eksik günleri 0 ile doldur (grafik sürekli olsun).
  const series = useMemo(() => {
    const byDay = new Map((d?.days ?? []).map((x) => [x.day, x]));
    return lastDays(range).map((day) => {
      const row = byDay.get(day);
      return { day, outQty: row?.outQty ?? 0, count: row?.count ?? 0 };
    });
  }, [d?.days, range]);
  const maxOut = Math.max(1, ...series.map((s) => s.outQty));
  const labelEvery = range === 30 ? 5 : 15;

  const runway = d?.runwayDays ?? null;
  // Stok bittiyse (tüketim verisi olsa da) yeterlilik "0 gün"dür — "veri yok" ile karıştırma.
  const depleted = d != null && d.onHand <= 0 && d.totals.d30 > 0;
  const runwayTone = depleted ? "var(--danger)" : runway == null ? "var(--neutral-500)" : runway < 7 ? "var(--danger)" : runway < 14 ? "var(--warning)" : "var(--success)";

  const stat = (label: string, val: string, opts?: { color?: string; hint?: string }) => (
    <div style={{ background: "var(--neutral-50)", borderRadius: "var(--radius-md)", padding: "10px 12px", minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--neutral-500)", marginBottom: 3 }}>{label}</div>
      <div className="mono" style={{ fontSize: 16, fontWeight: 600, color: opts?.color ?? "var(--neutral-900)", fontVariantNumeric: "tabular-nums" }}>{val}</div>
      {opts?.hint ? <div style={{ fontSize: 10.5, color: "var(--neutral-500)", marginTop: 2 }}>{opts.hint}</div> : null}
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="xl"
      title="Tüketim Trendi"
      subtitle={item ? `${item.name} · ${item.code}` : undefined}
    >
      {trendQ.isLoading ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--neutral-500)" }}>Yükleniyor…</div>
      ) : trendQ.isError ? (
        <div className="form-banner-err" role="alert">{trendQ.error?.message}</div>
      ) : d ? (
        <>
          {/* Pencere toplamları + eldeki + yeterlilik */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8, marginBottom: 16 }}>
            {stat("Son 1 Gün", `${fmtQty(d.totals.d1)} ${uom}`)}
            {stat("7 Gün", `${fmtQty(d.totals.d7)} ${uom}`)}
            {stat("30 Gün", `${fmtQty(d.totals.d30)} ${uom}`)}
            {stat("90 Gün", `${fmtQty(d.totals.d90)} ${uom}`)}
            {stat("Eldeki", `${fmtQty(d.onHand)} ${uom}`)}
            {stat("Yeterlilik", depleted ? "0 gün" : runway == null ? "—" : `~${fmtQty(Math.round(runway))} gün`, {
              color: runwayTone,
              hint: depleted ? "stok tükendi" : runway == null ? "tüketim verisi yok" : "eldeki ÷ 30g ort. tüketim",
            })}
          </div>

          {/* Günlük tüketim grafiği */}
          <div className="d-card" style={{ marginBottom: 14 }}>
            <div className="d-card-head">
              <div className="left"><span className="t">Günlük Tüketim</span><span className="s">çıkışlar: tüketim + sevkiyat + hurda</span></div>
              <div style={{ display: "flex", gap: 4 }}>
                {([30, 90] as const).map((r) => (
                  <button key={r} type="button" className={`btn-md ${range === r ? "primary" : "ghost"}`} style={{ height: 26, fontSize: 11.5, padding: "0 10px" }} onClick={() => setRange(r)} aria-pressed={range === r}>
                    {r} gün
                  </button>
                ))}
              </div>
            </div>
            <div className="d-card-body">
              {d.totals.d90 <= 0 ? (
                <div style={{ padding: "22px 0", textAlign: "center", color: "var(--neutral-500)", fontSize: 13 }}>
                  Son 90 günde tüketim hareketi yok.
                </div>
              ) : (
                <>
                  <div className="bar-chart" role="img" aria-label={`Son ${range} günde günlük tüketim, en yüksek ${fmtQty(maxOut)} ${uom}`}>
                    {series.map((s) => (
                      <div key={s.day} className="bar" data-val={`${s.day.slice(8, 10)}.${s.day.slice(5, 7)} · ${fmtQty(s.outQty)} ${uom} · ${s.count} hareket`}
                        style={{ height: `${Math.max(2, (s.outQty / maxOut) * 100)}%`, opacity: s.outQty === 0 ? 0.25 : 1 }} />
                    ))}
                  </div>
                  <div className="chart-x">
                    {series.map((s, i) => (
                      <span key={s.day}>{i % labelEvery === 0 ? `${s.day.slice(8, 10)}.${s.day.slice(5, 7)}` : ""}</span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Tür kırılımı (90 gün) */}
          {d.byType.length > 0 ? (
            <div className="d-card">
              <div className="d-card-head"><div className="left"><span className="t">Çıkış Kırılımı</span><span className="s">son 90 gün</span></div></div>
              <div className="d-card-body" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {d.byType.map((b) => {
                  const pct = d.totals.d90 > 0 ? (b.outQty / d.totals.d90) * 100 : 0;
                  return (
                    <div key={b.movementType} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 130, fontSize: 12.5, color: "var(--neutral-700)" }}>{TYPE_LABELS[b.movementType] ?? b.movementType}</span>
                      <div style={{ flex: 1, height: 8, background: "var(--neutral-100)", borderRadius: 999, overflow: "hidden" }} aria-hidden="true">
                        <div style={{ width: `${Math.max(2, pct)}%`, height: "100%", background: "var(--primary)", borderRadius: 999 }} />
                      </div>
                      <span className="mono" style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", minWidth: 110, textAlign: "right" }}>
                        {fmtQty(b.outQty)} {uom} <span style={{ color: "var(--neutral-500)" }}>%{fmtQty(Math.round(pct))}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <p style={{ fontSize: 11.5, color: "var(--neutral-500)", marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <Icon name="alertTriangle" size={12} />
            Transfer ve düzeltme hareketleri tüketim sayılmaz; yalnız üretim tüketimi, sevkiyat ve hurda çıkışları dahildir.
          </p>
        </>
      ) : null}
    </Drawer>
  );
}
